import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, PageHeader, StatusBadge } from "@/components/admin/AdminUI";
import { formatDate } from "@/lib/format";

export default function AdminDashboard() {
  const { data } = useQuery({ queryKey: ["admin-overview"], queryFn: async () => (await api.get("/admin/stats/overview")).data });

  const stat = (label, value, to) => (
    <Link to={to} className="block">
      <Card className="hover:shadow-sm transition-shadow">
        <div className="text-xs uppercase tracking-wider text-[#6C757D]">{label}</div>
        <div className="text-3xl font-semibold mt-1">{value ?? "—"}</div>
      </Card>
    </Link>
  );

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard" subtitle="Overview of your directory." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {stat("Brands", data?.total_brands, "/admin/brands")}
        {stat("Reviewed items", data?.reviewed_items, "/admin/clothing-items")}
        {stat("Approved items", data?.featured_items, "/admin/clothing-items?status=approved_featured")}
        {stat("Rejected items", data?.rejected_items, "/admin/clothing-items?status=not_sustainable")}
        {stat("Pending crawler", data?.pending_crawler_results, "/admin/brands")}
        {stat("Materials", data?.total_materials, "/admin/materials")}
        {stat("New reports", data?.false_advertising_new, "/admin/reports")}
        {stat("New contacts", data?.contact_submissions_new, "/admin/contacts")}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-semibold mb-3">Recent crawl runs</h3>
          <div className="space-y-2">
            {(data?.recent_crawl_runs || []).map((r) => (
              <Link key={r.id} to="/admin/crawl-runs" className="flex items-center justify-between border border-[#E9ECEF] rounded-md px-3 py-2 hover:bg-[#F1F3F5]">
                <div>
                  <div className="text-sm font-medium">{r.brand_name}</div>
                  <div className="text-xs text-[#6C757D]">{formatDate(r.created_at)} · {r.new_products_found || 0} new / {r.duplicate_products_found || 0} dup</div>
                </div>
                <StatusBadge status={r.status} />
              </Link>
            ))}
            {!(data?.recent_crawl_runs?.length) && <div className="text-sm text-[#6C757D]">No runs yet.</div>}
          </div>
        </Card>
        <Card>
          <h3 className="font-semibold mb-3">Recent contact submissions</h3>
          <div className="space-y-2">
            {(data?.recent_contact_submissions || []).map((r) => (
              <Link key={r.id} to="/admin/contacts" className="block border border-[#E9ECEF] rounded-md px-3 py-2 hover:bg-[#F1F3F5]">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{r.name} · {r.category}</div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="text-xs text-[#6C757D] truncate">{r.subject || r.message?.slice(0, 80)}</div>
              </Link>
            ))}
            {!(data?.recent_contact_submissions?.length) && <div className="text-sm text-[#6C757D]">No contacts yet.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
